import { useState } from 'react'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

const LIFESTYLE_META = {
  waterfront: { icon: '🌊', label: 'Waterfront', color: '#0891B2', bg: '#E0F2FE' },
  family:     { icon: '👨‍👩‍👧‍👦', label: 'Family',     color: '#16A34A', bg: '#DCFCE7' },
  luxury:     { icon: '💎', label: 'Luxury',     color: '#7C3AED', bg: '#EDE9FE' },
  investment: { icon: '📈', label: 'Investment', color: '#D97706', bg: '#FEF3C7' },
  golf:       { icon: '⛳', label: 'Golf Course', color: '#14532d', bg: '#bbf7d0' },
  urban:      { icon: '🏙️', label: 'Urban',       color: '#334155', bg: '#f1f5f9' },
  nature:     { icon: '🌳', label: 'Nature',      color: '#047857', bg: '#d1fae5' },
}

/**
 * Shared property card used on OffplanPage.
 * Accepts the normalized shape returned by the backend API + lifestyle tags.
 */
export default function PropertyCard({ project }) {
  const [hovered, setHovered] = useState(false)
  const { t } = useLanguage()
  const lifestyleTags = project.lifestyleTags || []

  const isProperty = Boolean(project.category || project.reference)
  const routePrefix = isProperty ? 'properties/detail' : 'offplan'
  
  const slugify = (text) => text?.toString().toLowerCase().trim().replace(/[\s\W-]+/g, '-') || 'property'
  
  let linkPath = project.id ? `/${routePrefix}/${project.id}` : '/contact'
  if (isProperty && project.id && project.name) {
    linkPath = `/${routePrefix}/${slugify(project.name)}/${project.id}`
  }

  const href = linkPath

  return (
    <a
      href={href}
      id={`property-card-${project.id || project.name?.replace(/\s+/g, '-').toLowerCase()}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        background: '#fff',
        border: '1px solid rgba(0,46,83,0.08)',
        boxShadow: hovered ? '0 20px 48px rgba(0,46,83,0.14)' : '0 4px 16px rgba(0,46,83,0.06)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'all 0.35s ease',
        textDecoration: 'none',
        cursor: 'pointer',
      }}
    >
      {/* ── Image ── */}
      <div style={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
        {project.image ? (
          <img
            src={project.image}
            alt={project.name}
            loading="lazy"
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: hovered ? 'scale(1.06)' : 'scale(1)',
              transition: 'transform 0.6s ease',
            }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: `linear-gradient(135deg, ${NAVY} 0%, #0a2d4a 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '48px',
          }}>
            🏙️
          </div>
        )}

        {/* Gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,20,40,0.65) 0%, transparent 55%)' }} />

        {/* Status badge */}
        <div style={{
          position: 'absolute', top: '14px', right: '14px',
          background: project.statusColor || GOLD, color: '#fff',
          fontFamily: FONT, fontSize: '11px', fontWeight: 700,
          padding: '4px 12px', borderRadius: '50px',
          letterSpacing: '0.5px',
        }}>
          {project.status}
        </div>

        {/* Developer badge */}
        {project.developer && project.developer.trim() !== 'Victory Homes' && (
          <div style={{
            position: 'absolute', top: '14px', left: '14px',
            background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            fontFamily: FONT, fontSize: '11px', fontWeight: 800,
            color: project.devColor || NAVY, padding: '5px 12px', borderRadius: '8px',
            letterSpacing: '0.3px', maxWidth: '140px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {project.developer}
          </div>
        )}

        {/* Lifestyle badges — bottom-right of image (max 2) */}
        {lifestyleTags.length > 0 && (
          <div style={{
            position: 'absolute', bottom: '44px', right: '10px',
            display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end',
          }}>
            {lifestyleTags.slice(0, 2).map(tag => {
              const meta = LIFESTYLE_META[tag]
              if (!meta) return null
              return (
                <div key={tag} style={{
                  background: meta.bg,
                  color: meta.color,
                  fontFamily: FONT, fontSize: '10px', fontWeight: 700,
                  padding: '3px 8px', borderRadius: '50px',
                  letterSpacing: '0.3px', whiteSpace: 'nowrap',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                }}>
                  {meta.icon} {t(`offplanDetail.tags.${tag}`) !== `offplanDetail.tags.${tag}` ? t(`offplanDetail.tags.${tag}`) : meta.label}
                </div>
              )
            })}
          </div>
        )}

        {/* Price overlay */}
        <div style={{
          position: 'absolute', bottom: '12px', left: '14px',
          fontFamily: FONT, fontSize: '19px', fontWeight: 700, color: '#fff', lineHeight: 1.2,
        }}>
          <div>{project.price?.startsWith('AED') || project.price?.startsWith('USD')
            ? `${t('offplanDetail.startingFrom')} ${project.price}`
            : project.price}
          </div>
          {project.sizeLabel && (
            <div style={{ fontSize: '11px', fontWeight: 500, color: 'rgba(255,255,255,0.78)', marginTop: '2px' }}>
              {project.sizeLabel}
            </div>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '18px 20px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: '9px' }}>

        {/* Name + Location */}
        <div>
          <div style={{ fontFamily: FONT, fontSize: '17px', fontWeight: 700, color: NAVY, marginBottom: '4px', lineHeight: 1.3 }}>
            {project.name}
          </div>
          <div style={{ fontFamily: FONT, fontSize: '13px', color: '#6b7a8d', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>📍</span> {project.area}{project.emirate ? `, ${project.emirate}` : ''}
          </div>
        </div>

        {/* Units row — shows beds and size */}
        {(project.beds || project.sizeLabel) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {project.beds && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px' }}>🏠</span>
                <span style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: NAVY }}>
                  {project.beds}
                </span>
              </div>
            )}
            {project.sizeLabel && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px' }}>📏</span>
                <span style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: NAVY }}>
                  {project.sizeLabel}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Meta row: type | handover | payment plan */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {[
            project.type ? project.type.split(', ').map(pt => t(`offplanDetail.types.${pt}`) !== `offplanDetail.types.${pt}` ? t(`offplanDetail.types.${pt}`) : pt).join(', ') : null,
            project.handover ? `${t('offplanDetail.handover')} ${project.handover}` : null,
          ]
            .filter(Boolean)
            .map((tag, i) => (
              <span key={i} style={{
                background: `${NAVY}0C`, color: '#4a5568',
                fontFamily: FONT, fontSize: '11px', fontWeight: 500,
                padding: '4px 9px', borderRadius: '6px',
              }}>
                {tag}
              </span>
            ))}

          {/* Payment plan pill */}
          {project.paymentPlan && (
            <span style={{
              background: `${GOLD}18`, color: '#92681f',
              fontFamily: FONT, fontSize: '11px', fontWeight: 700,
              padding: '4px 9px', borderRadius: '6px', border: `1px solid ${GOLD}30`,
            }}>
              {project.paymentPlan}
            </span>
          )}
        </div>

        {/* Amenities */}
        {project.amenities?.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {project.amenities.map((a, i) => (
              <span key={i} style={{
                fontFamily: FONT, fontSize: '11px', color: '#6b7a8d',
                display: 'flex', alignItems: 'center', gap: '3px',
              }}>
                <span style={{ color: GOLD, fontSize: '10px' }}>✦</span> {a}
              </span>
            ))}
          </div>
        )}

        {/* CTA — styled div (card itself is the link, no nested <a> needed) */}
        <div
          style={{
            marginTop: 'auto',
            display: 'block', textAlign: 'center',
            background: hovered ? NAVY : `${NAVY}10`,
            color: hovered ? '#fff' : NAVY,
            fontFamily: FONT, fontSize: '13px', fontWeight: 700,
            padding: '11px', borderRadius: '10px',
            transition: 'all 0.25s ease',
            border: `1.5px solid ${hovered ? NAVY : `${NAVY}20`}`,
          }}
        >
          { project.id ? t('offplanDetail.getBrochure') : t('props.enquireNow') }
        </div>

        {/* Updated at */}
        {project.updatedAt && (
          <div style={{ fontFamily: FONT, fontSize: '10px', color: '#9ca3af', textAlign: 'right', marginTop: '2px' }}>
            {project.updatedAt}
          </div>
        )}
      </div>
    </a>
  )
}
